import React, { useState,useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import api from "../../api/axios";
import Underline from "@tiptap/extension-underline";

import {
  Box,
  IconButton,
  Divider,
  Paper,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";

import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Title,
  Undo,
  Redo,
  Image as ImageIcon,
  AutoFixHigh,
  Visibility,
  Edit,
} from "@mui/icons-material";


const ToolbarButton = ({ onClick, active, icon, label, disabled }) => (
  <Tooltip title={label}>
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onClick}
        sx={{
          color: active ? "#1976d2" : "#444",
          bgcolor: active ? "rgba(25,118,210,0.08)" : "transparent",
        }}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

const RichTextEditor = ({ value, onChange }) => {
  const [mode, setMode] = useState("edit");
  const [aiLoading, setAiLoading] = useState(false);
  


  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image,
      Placeholder.configure({
        placeholder: "Write a detailed product description…",
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

    useEffect(() => {
  if (editor && value !== editor.getHTML()) {
    editor.commands.setContent(value || "");
  }
}, [value, editor]);
  if (!editor) return null;


  const addImage = () => {
    const url = prompt("Enter image URL");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  /* =========================
     AI REWRITE HANDLER
     ========================= */

//http://localhost:5000/api/ai/rewrite
const rewriteWithAI = async () => {
  try {
    setAiLoading(true);

    const res = await api.post("/ai/rewrite", {
      text: editor.getHTML(),
    });

    editor.commands.setContent(res.data.rewritten);
  } catch {
    alert("AI rewrite failed");
  } finally {
    setAiLoading(false);
  }
};


  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: "#d5d9d9",
        overflow: "hidden",
      }}
    >
      {/* TOP BAR */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
          py: 0.5,
          bgcolor: "#eef3f8",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {/* EDIT / PREVIEW TOGGLE */}
        <ToggleButtonGroup
          size="small"
          value={mode}
          exclusive
          onChange={(e, val) => val && setMode(val)}
        >
          <ToggleButton value="edit">
            <Edit fontSize="small" />
          </ToggleButton>
          <ToggleButton value="preview">
            <Visibility fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* AI BUTTON */}
        <Tooltip title="Rewrite using AI">
          <span>
            <IconButton
              size="small"
              onClick={rewriteWithAI}
              disabled={aiLoading || mode === "preview"}
              sx={{
  color: "#6f42c1",
  bgcolor: "rgba(111,66,193,0.1)",
  "&:hover": { bgcolor: "rgba(111,66,193,0.2)" },
}}

            >
              {aiLoading ? (
                <CircularProgress size={18} />
              ) : (
                <AutoFixHigh />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* TOOLBAR (ONLY IN EDIT MODE) */}
      {mode === "edit" && (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderBottom: "1px solid #e0e0e0",
            flexWrap: "wrap",
          }}
        >
          <ToolbarButton
            label="Bold"
            icon={<FormatBold />}
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <ToolbarButton
            label="Italic"
            icon={<FormatItalic />}
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <ToolbarButton
            label="Underline"
            icon={<FormatUnderlined />}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
          <ToolbarButton
            label="Strike"
            icon={<StrikethroughS />}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          />

          <Divider orientation="vertical" flexItem />

          <ToolbarButton
            label="Heading"
            icon={<Title />}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          />

          <ToolbarButton
            label="Bullet List"
            icon={<FormatListBulleted />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <ToolbarButton
            label="Numbered List"
            icon={<FormatListNumbered />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />

          <ToolbarButton
            label="Quote"
            icon={<FormatQuote />}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          />

          <Divider orientation="vertical" flexItem />

          <ToolbarButton
            label="Insert Image"
            icon={<ImageIcon />}
            onClick={addImage}
          />

          <Divider orientation="vertical" flexItem />

          <ToolbarButton
            label="Undo"
            icon={<Undo />}
            onClick={() => editor.chain().focus().undo().run()}
          />
          <ToolbarButton
            label="Redo"
            icon={<Redo />}
            onClick={() => editor.chain().focus().redo().run()}
          />
        </Box>
      )}

      {/* CONTENT AREA */}
      <Box
        sx={{
          px: 2,
          py: 2,
          minHeight: 200,
          fontSize: 14,
          lineHeight: 1.7,
          "& img": {
            maxWidth: "100%",
            borderRadius: 8,
            margin: "10px 0",
          },
        }}
      >
        {mode === "edit" ? (
          <EditorContent editor={editor} />
        ) : (
          <Box
            dangerouslySetInnerHTML={{ __html: value }}
            sx={{ color: "#111" }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default RichTextEditor;
